import { createIcon } from "./icons";

/** À surveiller (orange) ou critique (rouge) ; sans niveau, la valeur est normale. */
export type FpsStatsLevel = "warn" | "bad";

/** Un groupe de chiffres de la fenêtre de statistiques : un titre et des lignes « libellé, valeur, niveau ». */
export interface FpsStatsSection {
  title: string;
  rows: [label: string, value: string, level?: FpsStatsLevel][];
}

export interface FpsMeterOptions {
  /**
   * Coché/décoché depuis la modale de diagnostic du widget — bascule l'affichage de la grille de
   * pathfinding pour toute la zone actuellement visible (voir `MapAdapter.showPathfindingDebugGrid`/
   * `computeAreaDebugGrid`, et le bouton "Diagnostic" évoqué dans `createTokaMap.ts`). `FpsMeter`
   * ne connaît ni `MapAdapter` ni `ShapeStore` (même convention que `RoutePanel`/`SearchBox`, voir
   * CLAUDE.md) : c'est à l'appelant de recalculer et d'afficher/masquer la grille lui-même.
   */
  onToggleDebugGrid?: (enabled: boolean) => void;
  /**
   * État initial de la case (voir `createTokaMap.ts`, qui le restaure depuis le localStorage partagé
   * par toute instance `@tokamap/core` — `FpsMeter` lui-même ne connaît pas cette persistance,
   * seulement l'état qu'on lui donne). Par défaut : `false`.
   */
  initialDebugGridEnabled?: boolean;
  /**
   * Les chiffres de la fenêtre de statistiques, relus deux fois par seconde tant qu'elle est
   * affichée. Absente : la modale ne propose pas la fenêtre.
   */
  getRenderStats?: () => FpsStatsSection[];
  initialStatsVisible?: boolean;
  onToggleStats?: (visible: boolean) => void;
  /** Le banc d'essai : absent, la modale ne le propose pas. `text` est ce que « Copier » met dans le presse-papiers. */
  runBenchmark?: (onProgress: (done: number, total: number, label: string) => void, signal: AbortSignal) => Promise<FpsBenchmarkReport>;
  /** L'adresse où la carte s'ouvre comme pour un visiteur, banc d'essai lancé ; `null` tant qu'elle n'existe pas. */
  visitorTestUrl?: () => string | null;
}

export interface FpsBenchmarkReport {
  /** De 0 à 100. */
  score: number;
  /** Ce dont la note est faite (naviguer, arriver sur la carte), quand elle en mêle plusieurs. */
  parts: { label: string; score: number }[];
  grade: string;
  /** Une phrase pour n'importe quel utilisateur. */
  summary: string;
  /** Du plus au moins coûteux : ce que l'auteur de la carte peut changer, et les chiffres qui le justifient. */
  advice: { title: string; detail: string; technical: string }[];
  /** À lire avant la note : une mesure estimée, des ressources venues du cache. */
  notes: string[];
  /** Chiffres hors du tableau : temps de réponse, affichage du fond de carte, mémoire. */
  details: string[];
  header: string[];
  rows: string[][];
  warning: string | null;
  text: string;
}

/** Couleur d'une appréciation : vert, orange ou rouge selon la note. */
function scoreLevel(score: number): "good" | "warn" | "bad" {
  return score >= 70 ? "good" : score >= 50 ? "warn" : "bad";
}

function element<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/**
 * Compteur FPS live superposé à l'écran (outil de diagnostic, voir main.ts) — porte aussi, depuis
 * son propre bouton diagnostic, les options de diagnostic ponctuelles de l'éditeur (pour l'instant :
 * la grille de pathfinding, voir `FpsMeterOptions.onToggleDebugGrid`) plutôt qu'un widget séparé :
 * décision produit explicite (voir historique de conception dans le CLAUDE.md) après avoir écarté
 * un bouton dans `Header`/`SettingsSidebar`, jugés trop visibles ou réservés aux réglages persistés.
 */
export class FpsMeter {
  private el: HTMLDivElement;
  private fpsText: HTMLSpanElement;
  private modalBackdrop: HTMLDivElement | null = null;
  private checkboxInput: HTMLInputElement | null = null;
  private debugGridEnabled: boolean;
  private statsPanel: HTMLDivElement | null = null;
  private benchPanel: HTMLDivElement | null = null;
  private benchAbort: AbortController | null = null;
  private statsVisible: boolean;
  private frameCount = 0;
  private lastSampleAt = performance.now();
  private rafId: number;

  private readonly options: FpsMeterOptions;

  constructor(
    private container: HTMLElement,
    options: FpsMeterOptions,
  ) {
    this.debugGridEnabled = options.initialDebugGridEnabled ?? false;
    this.statsVisible = !!options.getRenderStats && (options.initialStatsVisible ?? false);
    this.options = options;

    this.el = document.createElement("div");
    this.el.className = "fps-meter";

    this.fpsText = document.createElement("span");
    this.fpsText.textContent = "… fps";
    this.el.appendChild(this.fpsText);

    if (options.onToggleDebugGrid || options.getRenderStats || options.runBenchmark) {
      const diagnosticsButton = document.createElement("button");
      diagnosticsButton.type = "button";
      diagnosticsButton.className = "fps-meter__diagnostics-button";
      diagnosticsButton.setAttribute("aria-label", "Diagnostic");
      diagnosticsButton.appendChild(createIcon("debug"));
      diagnosticsButton.addEventListener("click", () => this.openModal(options));
      this.el.appendChild(diagnosticsButton);
    }

    container.appendChild(this.el);
    if (this.statsVisible) this.showStats();
    this.rafId = requestAnimationFrame(this.tick);
  }

  /**
   * Une vraie modale (fond + panneau centré, fermeture explicite) plutôt qu'un popup ancré au
   * widget — un popup fermé sur "clic extérieur" s'est avéré se fermer aussi sur le clic qui coche
   * la case elle-même (le `click` de la case remonte jusqu'au listener document AVANT que le
   * navigateur ne déclenche son propre `change`, qui ne partait alors plus jamais) : ici, seul un
   * clic dont la CIBLE est le fond lui-même ferme la modale (voir `handleBackdropClick`), jamais un
   * clic sur son contenu — la case peut donc être cochée/décochée sans jamais fermer la modale.
   */
  private openModal(options: FpsMeterOptions): void {
    if (this.modalBackdrop) return;

    const backdrop = document.createElement("div");
    backdrop.className = "fps-meter__diagnostics-backdrop";
    backdrop.addEventListener("click", this.handleBackdropClick);

    const modal = document.createElement("div");
    modal.className = "fps-meter__diagnostics-modal";

    const header = document.createElement("div");
    header.className = "fps-meter__diagnostics-modal-header";
    const title = document.createElement("span");
    title.textContent = "Diagnostic";
    header.appendChild(title);
    const closeButton = document.createElement("button");
    closeButton.type = "button";
    closeButton.className = "fps-meter__diagnostics-modal-close";
    closeButton.setAttribute("aria-label", "Fermer");
    closeButton.appendChild(createIcon("close"));
    closeButton.addEventListener("click", () => this.closeModal());
    header.appendChild(closeButton);
    modal.appendChild(header);

    const row = document.createElement("label");
    row.className = "fps-meter__diagnostics-modal-row";

    // Interrupteur stylé plutôt que la case native du navigateur (voir historique de conception :
    // "la modale est vraiment moche") — reste une vraie `<input type="checkbox">` en dessous (donc
    // toujours accessible/clavier), seulement son apparence est redessinée via
    // `.fps-meter__switch-track` (voir style.css).
    const switchWrap = document.createElement("span");
    switchWrap.className = "fps-meter__switch";
    const checkbox = document.createElement("input");
    checkbox.type = "checkbox";
    checkbox.className = "fps-meter__switch-input";
    checkbox.checked = this.debugGridEnabled;
    checkbox.addEventListener("change", () => {
      this.debugGridEnabled = checkbox.checked;
      options.onToggleDebugGrid?.(this.debugGridEnabled);
    });
    const track = document.createElement("span");
    track.className = "fps-meter__switch-track";
    switchWrap.appendChild(checkbox);
    switchWrap.appendChild(track);

    row.appendChild(switchWrap);
    row.appendChild(document.createTextNode("Grille de pathfinding"));
    modal.appendChild(row);
    this.checkboxInput = checkbox;

    // Même convention que les fields de la sidebar (`AreaThemeSection.tsx`, backoffice) : un texte
    // atténué sous le contrôle plutôt qu'un tooltip — explique ce que les couleurs représentent sans
    // avoir à deviner (voir `PATHFINDING_DEBUG_BLOCKED_LAYER_ID`/`PATHFINDING_DEBUG_CHEAP_LAYER_ID`,
    // `MapLibreAdapter.ts`).
    const hint = document.createElement("p");
    hint.className = "fps-meter__diagnostics-modal-hint";
    hint.textContent = "Rouge : cellules bloquées par un obstacle. Jaune → bleu : cellules à coût réduit près d'une forme attractive — plus la teinte tire vers le bleu, plus l'attractivité est forte.";
    if (options.onToggleDebugGrid) modal.appendChild(hint);
    else row.remove();

    if (options.getRenderStats) {
      const statsRow = this.switchRow("Statistiques de rendu", this.statsVisible, (checked) => {
        if (checked) this.showStats();
        else this.hideStats();
        options.onToggleStats?.(checked);
      });
      modal.appendChild(statsRow);
      const statsHint = document.createElement("p");
      statsHint.className = "fps-meter__diagnostics-modal-hint";
      statsHint.textContent = "Temps de rendu par image, objets 3D revus et dessinés, appels de dessin et à-coups, sur la dernière seconde.";
      modal.appendChild(statsHint);
    }

    if (options.runBenchmark) {
      const runBenchmark = options.runBenchmark;
      const actions = document.createElement("div");
      actions.className = "fps-meter__diagnostics-modal-actions";
      const button = document.createElement("button");
      button.type = "button";
      button.className = "fps-meter__diagnostics-modal-action";
      button.textContent = "Lancer le banc d'essai";
      button.addEventListener("click", () => {
        this.closeModal();
        void this.startBenchmark(runBenchmark);
      });
      actions.appendChild(button);
      const visitorUrl = options.visitorTestUrl?.();
      if (options.visitorTestUrl) {
        const visitor = document.createElement("button");
        visitor.type = "button";
        visitor.className = "fps-meter__diagnostics-modal-action";
        visitor.textContent = "Tester comme un visiteur";
        visitor.disabled = !visitorUrl;
        visitor.addEventListener("click", () => {
          if (visitorUrl) window.open(visitorUrl, "_blank", "noopener");
        });
        actions.appendChild(visitor);
      }
      modal.appendChild(actions);
      const benchHint = document.createElement("p");
      benchHint.className = "fps-meter__diagnostics-modal-hint";
      const visitorText = !options.visitorTestUrl
        ? ""
        : visitorUrl
          ? " « Tester comme un visiteur » le fait sur la carte publiée, chargement réel compris (Ctrl+F5 pour ignorer le cache)."
          : " « Tester comme un visiteur » : une fois la carte publiée.";
      benchHint.textContent = `Mesure la fluidité de la carte et son arrivée, en deux à trois minutes : fenêtre au premier plan, sans toucher à la carte.${visitorText}`;
      modal.appendChild(benchHint);
    }

    backdrop.appendChild(modal);
    // Sur `container` (voir `TokaMapConfig`/`createTokaMap.ts`) — jamais `document.body` — même
    // convention que le reste de `ui/*` : la modale ne couvre que la zone de la carte, pas toute la
    // page (une `TopBar` au-dessus, côté exemple, reste par ex. cliquable).
    this.container.appendChild(backdrop);
    this.modalBackdrop = backdrop;

    document.addEventListener("keydown", this.handleEscapeKey);
  }

  private switchRow(label: string, checked: boolean, onChange: (checked: boolean) => void): HTMLLabelElement {
    const row = document.createElement("label");
    row.className = "fps-meter__diagnostics-modal-row";
    const switchWrap = document.createElement("span");
    switchWrap.className = "fps-meter__switch";
    const checkbox = document.createElement("input");
    checkbox.type = "checkbox";
    checkbox.className = "fps-meter__switch-input";
    checkbox.checked = checked;
    checkbox.addEventListener("change", () => onChange(checkbox.checked));
    const track = document.createElement("span");
    track.className = "fps-meter__switch-track";
    switchWrap.appendChild(checkbox);
    switchWrap.appendChild(track);
    row.appendChild(switchWrap);
    row.appendChild(document.createTextNode(label));
    return row;
  }

  /** Lance le banc d'essai sans passer par la fenêtre de diagnostic (voir `TokaMapBenchmarkConfig.autoRun`). */
  runBenchmark(): void {
    if (this.options.runBenchmark) void this.startBenchmark(this.options.runBenchmark);
  }

  /**
   * Le banc d'essai occupe tout l'écran : un clic ou un geste sur la carte fausserait la mesure, et un
   * onglet quitté la rend inutilisable — elle s'interrompt alors d'elle-même.
   */
  private async startBenchmark(run: NonNullable<FpsMeterOptions["runBenchmark"]>): Promise<void> {
    if (this.benchAbort) return;
    const abort = new AbortController();
    this.benchAbort = abort;
    let leftTab = false;
    const onVisibility = () => {
      if (!document.hidden) return;
      leftTab = true;
      abort.abort();
    };
    document.addEventListener("visibilitychange", onVisibility);

    const card = this.openBenchOverlay();
    const progress = element("div", "toka-bench__progress");
    const bar = element("div", "toka-bench__progress-bar");
    progress.appendChild(bar);
    const step = element("div", "toka-bench__step");
    const stepLabel = element("span", "", "Préparation…");
    step.append(element("span", "toka-bench__spinner"), stepLabel);
    card.replaceChildren(
      element("div", "toka-bench__title", "Mesure des performances en cours"),
      element("p", "toka-bench__lead", "Restez sur cet onglet et gardez la fenêtre au premier plan. Ne touchez à rien : la carte bouge toute seule pendant deux à trois minutes."),
      progress,
      step,
      // Fermé aussitôt : la mesure en cours s'interrompt d'elle-même, son résultat tardif est ignoré.
      this.benchButton("Arrêter", () => this.closeBench(), "toka-bench__button"),
    );
    try {
      const report = await run((done, total, label) => {
        bar.style.width = `${Math.round((done / total) * 100)}%`;
        stepLabel.textContent = `Étape ${Math.min(done + 1, total)} sur ${total} — ${label}`;
      }, abort.signal);
      if (leftTab) this.showBenchMessage(card, "Mesure interrompue", "L'onglet a été quitté : le navigateur ralentit alors l'affichage et les chiffres seraient faux. Relancez la mesure en restant sur cet onglet.");
      else if (!abort.signal.aborted) this.showBenchReport(card, report);
    } catch (error) {
      if (!abort.signal.aborted || leftTab) this.showBenchMessage(card, "Mesure impossible", error instanceof Error ? error.message : String(error));
    } finally {
      document.removeEventListener("visibilitychange", onVisibility);
      this.benchAbort = null;
    }
  }

  private showBenchMessage(card: HTMLDivElement, title: string, text: string): void {
    card.replaceChildren(
      element("div", "toka-bench__title", title),
      element("p", "toka-bench__lead", text),
      this.benchButton("Fermer", () => this.closeBench(), "toka-bench__button"),
    );
  }

  private showBenchReport(card: HTMLDivElement, report: FpsBenchmarkReport): void {
    const level = scoreLevel(report.score);
    const head = element("div", "toka-bench__score-row");
    const score = element("div", `toka-bench__score toka-bench__score--${level}`);
    score.append(element("span", "toka-bench__score-value", String(report.score)), element("span", "toka-bench__score-max", "/100"));
    const verdict = element("div", "toka-bench__verdict");
    verdict.append(element("div", `toka-bench__grade toka-bench__grade--${level}`, report.parts.length ? report.grade : `Fluidité ${report.grade.toLowerCase()}`), element("p", "toka-bench__summary", report.summary));
    if (report.parts.length) {
      const parts = element("div", "toka-bench__parts");
      for (const part of report.parts) {
        const chip = element("span", "toka-bench__part", `${part.label} `);
        chip.appendChild(element("strong", `toka-bench__part-score toka-bench__grade--${scoreLevel(part.score)}`, `${part.score}/100`));
        parts.appendChild(chip);
      }
      verdict.appendChild(parts);
    }
    head.append(score, verdict);

    const children: HTMLElement[] = [element("div", "toka-bench__title", "Résultat du banc d'essai"), head];
    if (report.warning) children.push(element("div", "toka-bench__warning", report.warning));
    for (const note of report.notes) children.push(element("div", "toka-bench__warning", note));
    if (report.advice.length) {
      children.push(element("div", "toka-bench__section", "Ce qui peut être amélioré"));
      const list = element("ol", "toka-bench__advice");
      for (const advice of report.advice) {
        const item = document.createElement("li");
        item.append(element("div", "toka-bench__advice-title", advice.title), element("div", "toka-bench__advice-detail", advice.detail));
        list.appendChild(item);
      }
      children.push(list);
    } else {
      children.push(element("p", "toka-bench__lead", "Rien de particulier à améliorer."));
    }
    children.push(element("p", "toka-bench__note", "Mesuré sur cet ordinateur : sur un téléphone ou un ordinateur plus modeste, la carte sera moins fluide."));

    const details = element("details", "toka-bench__details");
    details.appendChild(element("summary", "", "Détails techniques"));
    for (const advice of report.advice) details.appendChild(element("p", "toka-bench__technical", `${advice.title.split(" — ")[0]} : ${advice.technical}`));
    for (const line of report.details) details.appendChild(element("p", "toka-bench__technical", line));
    const table = element("table", "toka-bench__table");
    const headRow = table.createTHead().insertRow();
    for (const cell of report.header) headRow.appendChild(document.createElement("th")).textContent = cell;
    const body = table.createTBody();
    for (const row of report.rows) {
      const line = body.insertRow();
      for (const cell of row) line.insertCell().textContent = cell;
    }
    details.appendChild(table);
    children.push(details);

    const actions = element("div", "toka-bench__actions");
    const copy = this.benchButton("Copier le rapport", () => {
      void navigator.clipboard?.writeText(report.text).then(() => {
        copy.textContent = "Copié";
      });
    }, "toka-bench__button toka-bench__button--secondary");
    actions.append(copy, this.benchButton("Fermer", () => this.closeBench(), "toka-bench__button"));
    children.push(actions);
    card.replaceChildren(...children);
  }

  /** Sur la page entière et non sur la carte seule : rien du studio ne doit rester cliquable pendant la mesure. */
  private openBenchOverlay(): HTMLDivElement {
    this.benchPanel?.remove();
    const overlay = element("div", "toka-bench");
    const card = element("div", "toka-bench__card");
    overlay.appendChild(card);
    document.body.appendChild(overlay);
    this.benchPanel = overlay;
    return card;
  }

  private closeBench(): void {
    this.benchAbort?.abort();
    this.benchPanel?.remove();
    this.benchPanel = null;
  }

  private benchButton(label: string, onClick: () => void, className: string): HTMLButtonElement {
    const button = document.createElement("button");
    button.type = "button";
    button.className = className;
    button.textContent = label;
    button.addEventListener("click", onClick);
    return button;
  }

  private showStats(): void {
    this.statsVisible = true;
    if (this.statsPanel) return;
    this.statsPanel = document.createElement("div");
    this.statsPanel.className = "fps-meter__stats";
    this.container.appendChild(this.statsPanel);
    this.renderStats();
  }

  private hideStats(): void {
    this.statsVisible = false;
    this.statsPanel?.remove();
    this.statsPanel = null;
  }

  private renderStats(): void {
    const panel = this.statsPanel;
    const sections = this.options.getRenderStats?.();
    if (!panel || !sections) return;
    panel.replaceChildren(...sections.map((section) => {
      const block = document.createElement("div");
      block.className = "fps-meter__stats-section";
      const title = document.createElement("div");
      title.className = "fps-meter__stats-title";
      title.textContent = section.title;
      block.appendChild(title);
      for (const [label, value, level] of section.rows) {
        const line = document.createElement("div");
        line.className = level ? `fps-meter__stats-row fps-meter__stats-row--${level}` : "fps-meter__stats-row";
        const name = document.createElement("span");
        name.textContent = label;
        const figure = document.createElement("span");
        figure.textContent = value;
        line.append(name, figure);
        block.appendChild(line);
      }
      return block;
    }));
  }

  private closeModal(): void {
    document.removeEventListener("keydown", this.handleEscapeKey);
    this.modalBackdrop?.remove();
    this.modalBackdrop = null;
    this.checkboxInput = null;
  }

  /**
   * Reflète un changement d'état survenu AILLEURS (autre onglet/instance, voir `createTokaMap.ts`'s
   * écouteur `"storage"`) — met juste la case à jour si la modale est ouverte, ne redéclenche jamais
   * `FpsMeterOptions.onToggleDebugGrid` (l'appelant a déjà décidé/appliqué le changement lui-même ;
   * le redéclencher ici boucherait en rappelant `localStorage.setItem` avec la même valeur, sans
   * incidence réelle mais sans utilité non plus).
   */
  setDebugGridEnabled(enabled: boolean): void {
    this.debugGridEnabled = enabled;
    if (this.checkboxInput) this.checkboxInput.checked = enabled;
  }

  private handleBackdropClick = (event: MouseEvent): void => {
    if (event.target === this.modalBackdrop) this.closeModal();
  };

  private handleEscapeKey = (event: KeyboardEvent): void => {
    if (event.key === "Escape") this.closeModal();
  };

  private tick = (): void => {
    this.frameCount++;
    const now = performance.now();
    const elapsed = now - this.lastSampleAt;
    if (elapsed >= 500) {
      const fps = (this.frameCount * 1000) / elapsed;
      this.fpsText.textContent = `${fps.toFixed(0)} fps`;
      this.frameCount = 0;
      this.lastSampleAt = now;
      if (this.statsVisible) this.renderStats();
    }
    this.rafId = requestAnimationFrame(this.tick);
  };

  destroy(): void {
    cancelAnimationFrame(this.rafId);
    this.closeModal();
    this.hideStats();
    this.closeBench();
    this.el.remove();
  }
}
